import {
	CircularProgress,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Pagination,
	Box,
	Collapse,
	IconButton,
	Stack,
} from '@mui/material'
import { StyledTableCell } from '../core/StyledTableCell'
import React, { useState, useEffect } from 'react'
import { useChangeOrderContext } from './ChangeOrderContext'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { statusOptions, ChangeOrder, ManualChangeOrderInfo } from '../../types/Work/ChangeOrder'
import ComponentsList from './ComponentsList'
import { useLocation } from 'react-router-dom'
import ChangeOrderHistory from './ChangeOrderHistory'
import ChangeOrderDetails from './ChangeOrderDetails'
import { getRevisionsOfChangeOrder } from '../../services/changeOrderService'

interface RowProps {
	changeOrder: ChangeOrder
	projectTimeZone: string | null
}

function Row({ changeOrder, projectTimeZone }: RowProps) {
	const location = useLocation()
	const { loadChangeOrders, revisions, setRevisions } = useChangeOrderContext()

	const { surveyId: fromNotificationSurveyId, key } =
		(location.state as {
			surveyId: string
			fromNotification: boolean
			searchWi: string
			key: number
		}) || {}
	const [open, setOpen] = useState(
		fromNotificationSurveyId ? parseInt(fromNotificationSurveyId) === changeOrder.id : false,
	)

	// Update open state when key changes
	useEffect(() => {
		if (fromNotificationSurveyId) {
			setOpen(parseInt(fromNotificationSurveyId) === changeOrder.id)
		}
	}, [fromNotificationSurveyId, key, changeOrder.id])

	const loadRevisions = async () => {
		const revisionsResponse = await getRevisionsOfChangeOrder(changeOrder.id)
		setRevisions((prev) => {
			const newMap = new Map(prev)
			newMap.set(changeOrder.id, revisionsResponse)
			return newMap
		})
	}

	useEffect(() => {
		if (open) {

			loadRevisions()
		}
	}, [open])

	const changeOrderRevisions = revisions.get(changeOrder.id)

	return (
		<>
			<TableRow
				hover
				onClick={() => setOpen(!open)}
				sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
			>
				<StyledTableCell>
					<IconButton
						aria-label='expand row'
						size='small'
						onClick={(e) => {
							e.stopPropagation()
							setOpen(!open)
						}}
					>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</StyledTableCell>
				<StyledTableCell align='center'>{changeOrder.id}</StyledTableCell>
				<StyledTableCell
					align='left'
					sx={{
						maxWidth: '15rem',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						whiteSpace: 'normal',
					}}
				>
					{changeOrder.surveyInfo?.workItemPath}
				</StyledTableCell>
				<StyledTableCell align='left'>{changeOrder.surveyInfo?.inspectionComment || changeOrder.manualInfo?.description}</StyledTableCell>
				<StyledTableCell align='center'>{changeOrder.reportedBy}</StyledTableCell>
				<StyledTableCell align='center'>
					{formatLocaleShortDateTime(changeOrder.reportedAt, projectTimeZone ?? 'UTC')}
				</StyledTableCell>
				<StyledTableCell align='center'>
					{formatLocaleShortDateTime(changeOrder.finalizedAt, projectTimeZone ?? 'UTC')}
				</StyledTableCell>
				<StyledTableCell align='center'>{statusOptions[changeOrder.lastStatus]}</StyledTableCell>
				<StyledTableCell align='center'>{changeOrder.source}</StyledTableCell>
			</TableRow>
			<TableRow>
				<StyledTableCell
					style={{ paddingBottom: 0, paddingTop: 0 }}
					colSpan={13}
					sx={{ backgroundColor: 'grey.200' }}
				>
					<Collapse in={open} timeout='auto' unmountOnExit>
						<Box sx={{ p: 2 }}>
							<Stack spacing={3} sx={{ maxWidth: '100%' }}>
								{changeOrder.surveyInfo && (
									<ComponentsList changeOrders={changeOrder.surveyInfo.changeOrderComponents} />
								)}
								{changeOrder.manualInfo && <ChangeOrderDetails manualInfo={changeOrder.manualInfo} finalRevision={changeOrderRevisions && changeOrderRevisions.length === 0} />}
								{/* History Section */}
								<ChangeOrderHistory
									changeOrderId={changeOrder.id}
									currentStatus={changeOrder.lastStatus}
									surveyFinalized={changeOrder.finalizedAt !== null}
									loadChangeOrders={loadChangeOrders}
								/>
								{changeOrderRevisions && changeOrderRevisions.map((revision, index) => (
									<>
										<ChangeOrderDetails key={index} manualInfo={revision.manualInfo!} parentId={changeOrder.id}
											revisionNumber={index + 1} finalRevision={index === changeOrderRevisions.length - 1}
											createdAt={revision.reportedAt} />
										<ChangeOrderHistory
											key={index}
											changeOrderId={revision.id}
											currentStatus={revision.lastStatus}
											surveyFinalized={revision.finalizedAt !== null}
											loadChangeOrders={loadChangeOrders}
										/>
									</>
								))}
							</Stack>
						</Box>
					</Collapse>
				</StyledTableCell>
			</TableRow>
		</>
	)
}

export default function ChangeOrderList() {
	const { changeOrders, isLoading, setFilters, filters, totalCount } = useChangeOrderContext()
	const { projectTimeZone } = useProjectFeaturesContext()
	return (
		<>
			<Paper elevation={2} sx={{ height: '100%', width: '100%' }}>
				<TableContainer
					sx={{
						height: '100%',
						width: '100%',
					}}
				>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<StyledTableCell />
								<StyledTableCell align='center'>ID</StyledTableCell>
								<StyledTableCell align='left'>Work Item Name</StyledTableCell>
								<StyledTableCell align='left'>Description</StyledTableCell>
								<StyledTableCell align='center'>Reported By</StyledTableCell>
								<StyledTableCell align='center'>Reported On</StyledTableCell>
								<StyledTableCell align='center'>Finalized On</StyledTableCell>
								<StyledTableCell align='center'>Status</StyledTableCell>
								<StyledTableCell align='center'>Source</StyledTableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{isLoading ? (
								<TableRow>
									<StyledTableCell colSpan={13} align='center'>
										<CircularProgress />
									</StyledTableCell>
								</TableRow>
							) : (
								changeOrders.map((changeOrder) => (
									<Row
										key={changeOrder.id}
										changeOrder={changeOrder}
										projectTimeZone={projectTimeZone ?? null}
									/>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			<Box
				sx={{
					position: 'sticky',
					bottom: 0,
					py: 1,
					zIndex: 10,
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Pagination
					count={Math.ceil(totalCount / 10)}
					variant='outlined'
					shape='rounded'
					size='large'
					onChange={(_, page) => setFilters({ ...filters, page: page - 1 })}
					page={filters.page + 1}
				/>
			</Box>
		</>
	)
}
